/**
 * Whack-a-Mole rules (pure functions, no DOM). The server issues the round's
 * schedule ({ duration_ms, spawns: [[slot, at, visible], ...] }) and computes
 * the real score from the submitted hits; this module only decides which taps
 * count locally so the player shows the same numbers the server will.
 *
 * Mirrors App\Services\Games\WhackAMoleRound: a hit needs the target's own
 * hole, at least min_reaction_ms after it appeared, before it leaves, once per
 * target, and at least min_hit_gap_ms after the previous accepted tap. Which
 * image a target shows is decided by the server (round.images) and never
 * affects hits or scoring.
 */

/**
 * Spawns from the start response, or null when the shape is wrong. Each spawn
 * carries the server-assigned target image index (round.images; display only,
 * never part of the proof); an index outside the level's images falls back to
 * the first image.
 */
export function normalizeRound(round, imageCount) {
  if (!round || typeof round !== "object") return null;
  const duration = round.duration_ms;
  if (!Number.isInteger(duration) || duration <= 0 || !Array.isArray(round.spawns)) return null;
  const images = Array.isArray(round.images) ? round.images : [];
  const count = Number.isInteger(imageCount) && imageCount > 0 ? imageCount : 1;
  const spawns = [];
  for (let i = 0; i < round.spawns.length; i++) {
    const s = round.spawns[i];
    if (!Array.isArray(s) || s.length !== 3 || !s.every(Number.isInteger)) return null;
    const image = Number.isInteger(images[i]) && images[i] >= 0 && images[i] < count ? images[i] : 0;
    spawns.push({ index: i, slot: s[0], at: s[1], visible: s[2], end: s[1] + s[2], image });
  }
  return { durationMs: duration, spawns };
}

/**
 * The level's target images ([{ id, url, width, height }]): the collection
 * when present, else the single legacy target image, else [].
 */
export function targetImagesOf(assets) {
  if (!assets || typeof assets !== "object") return [];
  const valid = (a) => a && typeof a === "object" && typeof a.url === "string" && a.url !== "";
  if (Array.isArray(assets.targets)) {
    const list = assets.targets.filter(valid);
    if (list.length) return list;
  }
  return valid(assets.target) ? [assets.target] : [];
}

/**
 * The level's optional board background ({ url, width, height }) or null
 * (the default board). It is drawn behind the holes, cropped to the board
 * (cover, centred); the holes are laid out on the board grid, never on the image.
 */
export function backgroundOf(assets) {
  const bg = assets && typeof assets === "object" ? assets.background : null;
  return bg && typeof bg === "object" && typeof bg.url === "string" && bg.url !== "" ? bg : null;
}

/**
 * Image index shown in a hole at time t: that of the latest target to appear
 * there (so a target keeps its image while it sinks), 0 before any.
 */
export function imageInSlot(spawns, slot, t) {
  let image = 0;
  for (let i = 0; i < spawns.length; i++) {
    const s = spawns[i];
    if (s.at > t) break;
    if (s.slot === slot) image = s.image || 0;
  }
  return image;
}

/** Hole numbers 0..rows*cols-1 in reading order. */
export function slotList(rows, cols) {
  const count = Math.max(0, rows | 0) * Math.max(0, cols | 0);
  const slots = [];
  for (let i = 0; i < count; i++) slots.push(i);
  return slots;
}

/** Target showing in a hole at time t (ms since the round began), not yet hit. */
export function spawnInSlot(spawns, slot, t, hitIndexes) {
  for (let i = 0; i < spawns.length; i++) {
    const s = spawns[i];
    if (s.slot === slot && s.at <= t && t < s.end && !hitIndexes.has(s.index)) return s;
  }
  return null;
}

/** Targets visible at time t (hit targets stay listed so their feedback can play). */
export function visibleSpawns(spawns, t) {
  return spawns.filter((s) => s.at <= t && t < s.end);
}

/**
 * One tap at time t (ms since the round began) on a hole (or null = the board
 * between holes). Returns { kind: "hit", spawn, t } | { kind: "miss" } |
 * { kind: "early" } (target still rising, not counted) | { kind: "ignored" }
 * (repeat input inside the debounce gap, or outside the round).
 */
export function classifyTap(state, slot, t, rules) {
  const time = Math.floor(t);
  if (time < 0 || time > state.durationMs) return { kind: "ignored" };
  if (state.lastTapAt !== null && time - state.lastTapAt < rules.minHitGapMs) return { kind: "ignored" };
  if (slot === null || slot === undefined) return { kind: "miss", t: time };
  const spawn = spawnInSlot(state.spawns, slot, time, state.hitIndexes);
  if (!spawn) return { kind: "miss", t: time };
  if (time < spawn.at + rules.minReactionMs) return { kind: "early", t: time };
  return { kind: "hit", spawn, t: time };
}

/** Fresh tap state for a round. */
export function createTapState(round) {
  return { spawns: round.spawns, durationMs: round.durationMs, hitIndexes: new Set(), hits: [], misses: 0, lastTapAt: null };
}

/** Apply a classified tap; returns the same state object (mutated) for refs. */
export function applyTap(state, outcome) {
  if (outcome.kind === "hit") {
    state.hitIndexes.add(outcome.spawn.index);
    state.hits.push({ index: outcome.spawn.index, t: outcome.t, slot: outcome.spawn.slot });
    state.lastTapAt = outcome.t;
  } else if (outcome.kind === "miss") {
    state.misses += 1;
    state.lastTapAt = outcome.t;
  }
  return state;
}

/** Targets that left their hole before time t without being hit. */
export function escapedCount(spawns, t, hitIndexes) {
  let n = 0;
  for (let i = 0; i < spawns.length; i++) {
    if (spawns[i].end <= t && !hitIndexes.has(spawns[i].index)) n++;
  }
  return n;
}

/** Same formula as the server (display only; the server's score is final). */
export function scoreOf(config, hits, misses, escaped) {
  const total = hits * (config.points_per_hit | 0) - misses * (config.miss_penalty | 0) - escaped * (config.escape_penalty | 0);
  return Math.max(0, total);
}

/** Proof submitted to games/play/complete: [index, timeMs, slot] per hit, plus the miss count. */
export function proofOf(state) {
  return { hits: state.hits.map((h) => [h.index, h.t, h.slot]), misses: state.misses };
}

/** Number keys 1–9 pick holes in reading order. */
export function keyToSlot(key, slotCount) {
  if (typeof key !== "string" || key.length !== 1 || key < "1" || key > "9") return null;
  const slot = key.charCodeAt(0) - 49;
  return slot < slotCount ? slot : null;
}

/** Validated rules from the public level config (null when unusable). */
export function rulesOf(config) {
  if (!config || typeof config !== "object") return null;
  const rows = config.rows;
  const cols = config.cols;
  if (!Number.isInteger(rows) || !Number.isInteger(cols) || rows < 1 || cols < 1 || rows * cols > 30) return null;
  return {
    rows,
    cols,
    durationSeconds: config.duration_seconds | 0,
    targetScale: Math.min(100, Math.max(40, config.target_scale | 0 || 70)),
    requiredScore: config.required_score | 0,
    maxScore: config.max_score | 0,
    targets: config.targets | 0,
    maxActive: config.max_active | 0,
    pointsPerHit: config.points_per_hit | 0,
    missPenalty: config.miss_penalty | 0,
    escapePenalty: config.escape_penalty | 0,
    minReactionMs: Number.isInteger(config.min_reaction_ms) ? config.min_reaction_ms : 100,
    minHitGapMs: Number.isInteger(config.min_hit_gap_ms) ? config.min_hit_gap_ms : 80,
  };
}
